import React, { useState } from "react"
import * as Icons from "lucide-react"

const TABS = ["Home", "Send / Receive", "Folder", "View", "Help"]

// Outlook-style icon palette — each command reads at a glance by colour.
const C = {
  blue: "#2563eb",
  red: "#dc2626",
  green: "#16a34a",
  amber: "#d97706",
  purple: "#7c3aed",
  teal: "#0891b2",
  gold: "#f59e0b",
  gray: "#64748b",
  orange: "#ea580c",
  pink: "#db2777",
}

function Big({ icon: Icon, label, onClick, disabled, caret, color, active }) {
  return (
    <button
      className={`em-rb-big ${active ? "em-rb-active" : ""}`}
      onClick={onClick}
      disabled={disabled}
      title={label}
      type="button"
    >
      <Icon size={19} color={disabled ? undefined : color} />
      <span className="em-rb-big-label">
        {label}
        {caret && <Icons.ChevronDown size={9} />}
      </span>
    </button>
  )
}

function Small({ icon: Icon, label, onClick, disabled, caret, color, active }) {
  return (
    <button
      className={`em-rb-small ${active ? "em-rb-active" : ""}`}
      onClick={onClick}
      disabled={disabled}
      title={label}
      type="button"
    >
      <Icon size={14} color={disabled ? undefined : color} />
      <span>{label}</span>
      {caret && <Icons.ChevronDown size={9} />}
    </button>
  )
}

function Stack({ children }) {
  return <div className="em-rb-stack">{children}</div>
}

function Group({ label, children }) {
  return (
    <div className="em-rb-group">
      <div className="em-rb-buttons">{children}</div>
      <div className="em-rb-group-label">{label}</div>
    </div>
  )
}

export default function EmailRibbon({
  h = {},
  ui = {},
  collapsed = false,
  onToggleCollapsed,
}) {
  const [tab, setTab] = useState("Home")
  const [accountOpen, setAccountOpen] = useState(false)
  const has = !!ui.hasMessage
  const noop = () => {}
  const H = new Proxy(h, {
    get: (t, k) => (typeof t[k] === "function" ? t[k] : noop),
  })
  const acct = ui.account || {}

  return (
    <div className={`em-ribbon ${collapsed ? "em-ribbon-mini" : ""}`}>
      <div className="em-ribbon-tabs">
        {TABS.map(t => (
          <button
            key={t}
            className={`em-ribbon-tab ${
              tab === t ? "em-ribbon-tab-active" : ""
            }`}
            onClick={() => setTab(t)}
            type="button"
          >
            {t}
          </button>
        ))}

        {/* Logged-in account avatar + popover (top-right). */}
        <div className="em-ribbon-account">
          <button
            className="em-ribbon-avatar-btn"
            title={acct.email || acct.name || "Account"}
            onClick={() => setAccountOpen(o => !o)}
            type="button"
          >
            <span className="em-ribbon-username">
              {acct.name || acct.email || "Account"}
            </span>
            <span className="em-ribbon-avatar">{acct.initial || "U"}</span>
            <Icons.ChevronDown size={13} className="em-ribbon-avatar-caret" />
          </button>

          {accountOpen && (
            <>
              <div
                className="em-account-backdrop"
                onClick={() => setAccountOpen(false)}
              />
              <div className="em-account-pop">
                <div className="em-account-pop-main">
                  <span className="em-account-pop-avatar">
                    {acct.initial || "U"}
                  </span>
                  <div className="em-account-pop-info">
                    <div className="em-account-pop-name">
                      {acct.name || "Account"}
                    </div>
                    <div className="em-account-pop-email">
                      {acct.email || ""}
                    </div>
                    <button
                      className="em-account-pop-view"
                      onClick={() => {
                        setAccountOpen(false)
                        H.viewAccount()
                      }}
                      type="button"
                    >
                      View account
                    </button>
                  </div>
                </div>
                <button
                  className="em-account-pop-add"
                  onClick={() => {
                    setAccountOpen(false)
                    H.addAccount()
                  }}
                  type="button"
                >
                  <Icons.UserPlus size={18} /> Add an account
                </button>
              </div>
            </>
          )}
        </div>

        <button
          className="em-ribbon-toggle"
          title={collapsed ? "Show ribbon" : "Hide ribbon"}
          onClick={onToggleCollapsed}
          type="button"
        >
          {collapsed ? <Icons.ChevronsDown size={15} /> : <Icons.ChevronsUp size={15} />}
        </button>
      </div>

      <div className="em-rb-content">
        {tab === "Home" && (
          <>
            <Group label="New">
              <Big
                icon={Icons.Mail}
                label="New Email"
                color={C.blue}
                onClick={H.newMail}
              />
              <Big
                icon={Icons.FilePlus}
                label="New Items"
                color={C.blue}
                caret
                onClick={H.newMail}
              />
            </Group>
            <Group label="Delete">
              <Stack>
                <Small
                  icon={Icons.BellOff}
                  label="Ignore"
                  color={C.gray}
                  disabled={!has}
                  onClick={H.ignore}
                />
                <Small
                  icon={Icons.Eraser}
                  label="Clean Up"
                  color={C.teal}
                  caret
                  onClick={H.cleanUp}
                />
                <Small
                  icon={Icons.Ban}
                  label="Junk"
                  color={C.red}
                  caret
                  disabled={!has}
                  onClick={H.junk}
                />
              </Stack>
              <Big
                icon={Icons.Trash2}
                label="Delete"
                color={C.red}
                disabled={!has}
                onClick={H.delete}
              />
              <Big
                icon={Icons.Archive}
                label="Archive"
                color={C.amber}
                disabled={!has}
                onClick={H.archive}
              />
            </Group>
            <Group label="Respond">
              <Big
                icon={Icons.Reply}
                label="Reply"
                color={C.blue}
                disabled={!has}
                onClick={H.reply}
              />
              <Big
                icon={Icons.ReplyAll}
                label="Reply All"
                color={C.blue}
                disabled={!has}
                onClick={H.replyAll}
              />
              <Big
                icon={Icons.Forward}
                label="Forward"
                color={C.blue}
                disabled={!has}
                onClick={H.forward}
              />
              <Stack>
                <Small
                  icon={Icons.CalendarPlus}
                  label="Meeting"
                  color={C.green}
                  onClick={H.meeting}
                />
              </Stack>
            </Group>
            <Group label="Quick Steps">
              <Stack>
                <Small
                  icon={Icons.Clock}
                  label="TsTestReminder"
                  color={C.orange}
                  onClick={H.qsReminder}
                />
                <Small
                  icon={Icons.Users}
                  label="Team Email"
                  color={C.teal}
                  onClick={H.qsTeam}
                />
                <Small
                  icon={Icons.Trash}
                  label="Reply & Delete"
                  color={C.red}
                  disabled={!has}
                  onClick={H.qsReplyDelete}
                />
              </Stack>
              <Stack>
                <Small
                  icon={Icons.UserCheck}
                  label="To Manager"
                  color={C.purple}
                  disabled={!has}
                  onClick={H.qsToManager}
                />
                <Small
                  icon={Icons.CheckCircle2}
                  label="Done"
                  color={C.green}
                  disabled={!has}
                  onClick={H.qsDone}
                />
                <Small
                  icon={Icons.Plus}
                  label="Create New"
                  color={C.blue}
                  onClick={H.qsCreate}
                />
              </Stack>
            </Group>
            <Group label="Move">
              <Big
                icon={Icons.FolderInput}
                label="Move"
                color={C.blue}
                caret
                disabled={!has}
                onClick={H.move}
              />
              <Big
                icon={Icons.ListChecks}
                label="Rules"
                color={C.purple}
                caret
                onClick={H.rules}
              />
              <Big
                icon={Icons.StickyNote}
                label="Send to OneNote"
                color={C.purple}
                disabled={!has}
                onClick={H.oneNote}
              />
            </Group>
            <Group label="Tags">
              <Big
                icon={Icons.MailOpen}
                label="Unread/ Read"
                color={C.blue}
                disabled={!has}
                onClick={H.unreadRead}
              />
              <Big
                icon={Icons.Tag}
                label="Categorise"
                color={C.orange}
                caret
                disabled={!has}
                onClick={H.categorise}
              />
              <Big
                icon={Icons.Flag}
                label="Follow Up"
                color={C.red}
                caret
                disabled={!has}
                onClick={H.followUp}
              />
            </Group>
            <Group label="Find">
              <Stack>
                <div className="em-rb-people" onClick={H.searchPeople}>
                  <Icons.Search size={13} color={C.gray} />
                  <input placeholder="Search People" readOnly />
                </div>
                <Small
                  icon={Icons.BookUser}
                  label="Address Book"
                  color={C.teal}
                  onClick={H.addressBook}
                />
                <Small
                  icon={Icons.Filter}
                  label="Filter Email"
                  color={C.gray}
                  caret
                  onClick={H.filterEmail}
                />
              </Stack>
            </Group>
            <Group label="Speech">
              <Big
                icon={Icons.Volume2}
                label="Read Aloud"
                color={C.blue}
                disabled={!has}
                onClick={H.readAloud}
              />
            </Group>
            <Group label="Language">
              <Big
                icon={Icons.Languages}
                label="Translate"
                color={C.green}
                caret
                disabled={!has}
                onClick={H.translate}
              />
            </Group>
            <Group label="Add-ins">
              <Big
                icon={Icons.Puzzle}
                label="Get Add-ins"
                color={C.purple}
                onClick={H.getAddins}
              />
            </Group>
          </>
        )}

        {tab === "Send / Receive" && (
          <>
            <Group label="Send &amp; Receive">
              <Big
                icon={Icons.RefreshCw}
                label="Send/Receive All Folders"
                color={C.green}
                onClick={H.srAll}
              />
              <Stack>
                <Small
                  icon={Icons.FolderSync}
                  label="Update Folder"
                  color={C.green}
                  onClick={H.updateFolder}
                />
                <Small
                  icon={Icons.Send}
                  label="Send All"
                  color={C.blue}
                  onClick={H.sendAll}
                />
                <Small
                  icon={Icons.Users}
                  label="Send/Receive Groups"
                  color={C.teal}
                  caret
                  onClick={H.srGroups}
                />
              </Stack>
            </Group>
            <Group label="Download">
              <Big
                icon={Icons.Activity}
                label="Show Progress"
                color={C.blue}
                onClick={H.showProgress}
              />
              <Big
                icon={Icons.XCircle}
                label="Cancel All"
                color={C.red}
                onClick={H.cancelAll}
              />
            </Group>
            <Group label="Server">
              <Big
                icon={Icons.FileDown}
                label="Download Headers"
                color={C.blue}
                onClick={H.downloadHeaders}
              />
              <Stack>
                <Small
                  icon={Icons.ArrowDownToLine}
                  label="Mark to Download"
                  color={C.green}
                  caret
                  onClick={H.markDownload}
                />
                <Small
                  icon={Icons.Square}
                  label="Unmark to Download"
                  color={C.gray}
                  caret
                  onClick={H.unmarkDownload}
                />
                <Small
                  icon={Icons.CheckSquare}
                  label="Process Marked Headers"
                  color={C.green}
                  onClick={H.processHeaders}
                />
              </Stack>
            </Group>
            <Group label="Preferences">
              <Big
                icon={Icons.Settings}
                label="Download Preferences"
                color={C.gray}
                caret
                onClick={H.downloadPrefs}
              />
              <Big
                icon={Icons.WifiOff}
                label="Work Offline"
                color={C.orange}
                active={ui.workOffline}
                onClick={H.workOffline}
              />
            </Group>
          </>
        )}

        {tab === "Folder" && (
          <>
            <Group label="New">
              <Big
                icon={Icons.FolderPlus}
                label="New Folder"
                color={C.gold}
                onClick={H.newFolder}
              />
              <Big
                icon={Icons.FolderSearch}
                label="New Search Folder"
                color={C.teal}
                onClick={H.newSearchFolder}
              />
            </Group>
            <Group label="Actions">
              <Stack>
                <Small
                  icon={Icons.PenLine}
                  label="Rename Folder"
                  color={C.blue}
                  onClick={H.renameFolder}
                />
                <Small
                  icon={Icons.Copy}
                  label="Copy Folder"
                  color={C.gray}
                  onClick={H.copyFolder}
                />
                <Small
                  icon={Icons.FolderInput}
                  label="Move Folder"
                  color={C.blue}
                  onClick={H.moveFolder}
                />
                <Small
                  icon={Icons.FolderMinus}
                  label="Delete Folder"
                  color={C.red}
                  onClick={H.deleteFolder}
                />
              </Stack>
            </Group>
            <Group label="Clean Up">
              <Big
                icon={Icons.CheckCheck}
                label="Mark All as Read"
                color={C.blue}
                onClick={H.markAllRead}
              />
              <Big
                icon={Icons.ListChecks}
                label="Run Rules Now"
                color={C.purple}
                onClick={H.runRules}
              />
              <Stack>
                <Small
                  icon={Icons.Eraser}
                  label="Clean Up Folder"
                  color={C.teal}
                  caret
                  onClick={H.cleanUpFolder}
                />
                <Small
                  icon={Icons.Trash2}
                  label="Delete All"
                  color={C.red}
                  onClick={H.deleteAll}
                />
                <Small
                  icon={Icons.RotateCcw}
                  label="Recover Deleted Items"
                  color={C.green}
                  onClick={H.recoverDeleted}
                />
              </Stack>
            </Group>
            <Group label="Favourites">
              <Big
                icon={Icons.Star}
                label="Add to Favourites"
                color={C.gold}
                onClick={H.addFavourites}
              />
            </Group>
            <Group label="Online View">
              <Big
                icon={Icons.Globe}
                label="View on Server"
                color={C.teal}
                onClick={H.viewOnServer}
              />
            </Group>
            <Group label="Properties">
              <Stack>
                <Small
                  icon={Icons.Archive}
                  label="Auto-archive Settings"
                  color={C.amber}
                  onClick={H.autoArchive}
                />
                <Small
                  icon={Icons.Shield}
                  label="Folder Permissions"
                  color={C.blue}
                  onClick={H.folderPermissions}
                />
                <Small
                  icon={Icons.Settings}
                  label="Folder Properties"
                  color={C.gray}
                  onClick={H.folderProperties}
                />
              </Stack>
            </Group>
          </>
        )}

        {tab === "View" && (
          <>
            <Group label="Current View">
              <Big
                icon={Icons.Eye}
                label="Change View"
                color={C.blue}
                caret
                onClick={H.changeView}
              />
              <Stack>
                <Small
                  icon={Icons.Settings}
                  label="View Settings"
                  color={C.gray}
                  onClick={H.viewSettings}
                />
                <Small
                  icon={Icons.RotateCcw}
                  label="Reset View"
                  color={C.orange}
                  onClick={H.resetView}
                />
              </Stack>
            </Group>
            <Group label="Messages">
              <Stack>
                <Small
                  icon={Icons.MessagesSquare}
                  label="Show as Conversations"
                  color={C.blue}
                  active={ui.showConversations}
                  onClick={H.showConversations}
                />
                <Small
                  icon={Icons.Settings2}
                  label="Conversation Settings"
                  color={C.gray}
                  caret
                  onClick={H.convSettings}
                />
              </Stack>
            </Group>
            <Group label="Arrangement">
              <Big
                icon={Icons.LayoutList}
                label="Message Preview"
                color={C.blue}
                caret
                active={ui.messagePreview}
                onClick={H.messagePreview}
              />
              <Stack>
                <Small
                  icon={Icons.Calendar}
                  label="Date"
                  color={C.blue}
                  active={ui.sortBy === "date"}
                  onClick={H.sortDate}
                />
                <Small
                  icon={Icons.Flag}
                  label="Flag Status"
                  color={C.red}
                  active={ui.sortBy === "flag"}
                  onClick={H.sortFlag}
                />
                <Small
                  icon={Icons.User}
                  label="From"
                  color={C.teal}
                  active={ui.sortBy === "from"}
                  onClick={H.sortFrom}
                />
              </Stack>
              <Stack>
                <Small
                  icon={Icons.ArrowDownUp}
                  label="Reverse Sort"
                  color={C.purple}
                  onClick={H.reverseSort}
                />
                <Small
                  icon={Icons.Columns}
                  label="Add Columns"
                  color={C.gray}
                  onClick={H.addColumns}
                />
                <Small
                  icon={Icons.ChevronsUpDown}
                  label="Expand/Collapse"
                  color={C.gray}
                  caret
                  onClick={H.expandCollapse}
                />
              </Stack>
            </Group>
            <Group label="Layout">
              <Big
                icon={Icons.AlignJustify}
                label="Use Tighter Spacing"
                color={C.gray}
                active={ui.density === "compact"}
                onClick={H.tighterSpacing}
              />
              <Big
                icon={Icons.PanelLeft}
                label="Folder Pane"
                color={C.blue}
                caret
                active={ui.folderPaneOpen}
                onClick={H.folderPane}
              />
              <Big
                icon={Icons.PanelRight}
                label="Reading Pane"
                color={C.blue}
                caret
                active={ui.readingPane}
                onClick={H.readingPane}
              />
              <Big
                icon={Icons.ListTodo}
                label="To-Do Bar"
                color={C.green}
                caret
                onClick={H.todoBar}
              />
            </Group>
            <Group label="Window">
              <Big
                icon={Icons.Bell}
                label="Reminders Window"
                color={C.orange}
                onClick={H.remindersWindow}
              />
              <Big
                icon={Icons.ExternalLink}
                label="Open in New Window"
                color={C.teal}
                disabled={!has}
                onClick={H.openNewWindow}
              />
              <Big
                icon={Icons.XSquare}
                label="Close All Items"
                color={C.red}
                onClick={H.closeAll}
              />
            </Group>
            <Group label="Immersive">
              <Big
                icon={Icons.BookOpen}
                label="Immersive Reader"
                color={C.purple}
                disabled={!has}
                onClick={H.immersiveReader}
              />
            </Group>
          </>
        )}

        {tab === "Help" && (
          <>
            <Group label="Help">
              <Big
                icon={Icons.HelpCircle}
                label="Help"
                color={C.blue}
                onClick={H.help}
              />
              <Big
                icon={Icons.MessageSquare}
                label="Feedback"
                color={C.green}
                onClick={H.feedback}
              />
              <Big
                icon={Icons.Lightbulb}
                label="Suggest a Feature"
                color={C.gold}
                onClick={H.suggest}
              />
              <Big
                icon={Icons.GraduationCap}
                label="Show Training"
                color={C.purple}
                onClick={H.training}
              />
              <Big
                icon={Icons.Wrench}
                label="Support Tool"
                color={C.teal}
                onClick={H.support}
              />
            </Group>
            <Group label="Tools">
              <Big
                icon={Icons.Activity}
                label="Get Diagnostics"
                color={C.blue}
                onClick={H.diagnostics}
              />
            </Group>
          </>
        )}
      </div>
    </div>
  )
}
